import { LineItem, Order, Refund } from "gpc-shared-types";
import { atom } from "jotai";
import { loadable } from "jotai/utils";

import { API, GraphQLResult } from "@aws-amplify/api";

import { customerAccessTokenAtom } from "./authAtoms";
import { getOrder, getOrderRefundable } from "../graphql/queries";
import {
  prepareOrderForRefundMutation,
  refundOrderMutation,
} from "../graphql/mutations";

export const selectedItemsAtom = atom<LineItem[]>([]);

export const stepAtom = atom<number>(1);

export const cancelReasonsAtom = atom<Promise<string[]>>(async () => {
  const response = await fetch(
    "https://api.storyblok.com/v2/cdn/stories/content/returns/reasons?cv=1677642111&token=EbYzLwVFIpONrWQWQtpXigtt&version=published",
  );

  const data = await response.json();

  return data.story.content.list.map((item: any) => item.DisplayName);
});
export const loadableCancelRasonsAtom = loadable(cancelReasonsAtom);

export type CancellationResult = {
  success: boolean;
  reason?: string;
  comments: string;
  canceledItems: any[];
};

export const cancelResultAtom = atom<CancellationResult | undefined>(undefined);

export type RefundLineItem = {
  id: string;
  quantity: number;
};

export const refundOrderLineItemsAtom = atom(
  null,
  async (
    get,
    set,
    orderId: string,
    lineItems: RefundLineItem[],
    reason: string,
    comments: string,
    resolution: string,
  ) => {
    const userAccessToken = get(customerAccessTokenAtom);

    if (!userAccessToken) {
      return;
    }

    const refundLineItemIds = lineItems.map((item) => item.id);

    set(cancelInProgressAtom, true);
    set(currentCancelStepAtom, 1);

    try {
      const preparationResponse = (await API.graphql({
        query: prepareOrderForRefundMutation,
        variables: {
          customerAccessToken: userAccessToken,
          orderId,
          lineItemIds: refundLineItemIds,
        },
      })) as GraphQLResult<{
        prepareOrderForRefund: Order;
      }>;

      if (preparationResponse.errors) {
        throw new Error(preparationResponse.errors[0].message);
      }

      let orderRefundable = false;

      while (!orderRefundable) {
        await new Promise((resolve) => setTimeout(resolve, 5000));

        const response = (await API.graphql({
          query: getOrderRefundable,
          variables: {
            customerAccessToken: userAccessToken,
            id: orderId,
          },
        })) as GraphQLResult<{
          order: Order;
        }>;

        if (response.errors) {
          throw new Error(response.errors[0].message);
        }

        orderRefundable = response.data?.order.refundable || false;
      }

      console.log("Order is refundable");

      const refundResponse = (await API.graphql({
        query: refundOrderMutation,
        variables: {
          customerAccessToken: userAccessToken,
          orderId,
          lineItems: lineItems.map((item) => ({
            lineItemId: item.id,
            quantity: item.quantity,
          })),
          reason,
          comments: comments,
          resolution,
        },
      })) as GraphQLResult<{
        refundOrder: Refund;
      }>;

      if (refundResponse.errors) {
        throw new Error(refundResponse.errors[0].message);
      }

      console.log("Refund responseeeee", refundResponse);

      set(cancelInProgressAtom, false);
      set(currentCancelStepAtom, 0);

      return refundResponse;
    } catch (error) {
      console.error(error);
      throw error;
    }
  },
);

export const cancelInProgressAtom = atom<boolean>(false);

export const currentCancelStepAtom = atom<number>(0);

export type RejectedCancellation = {
  reason?: string;
};

export const rejectedOrder = atom<RejectedCancellation>({});

export const shopCurrencyAtom = atom<string>("AUD");

export const currentOrderAtom = atom<Order | undefined>(undefined);

export const appLoadingAtom = atom<boolean>(true);

export const fetchOrderById = atom(
  null,
  async (get, set, orderId: string | undefined) => {
    set(appLoadingAtom, true);

    const userAccessToken = get(customerAccessTokenAtom);

    if (!orderId || !userAccessToken) {
      set(currentOrderAtom, undefined);
      return;
    }

    try {
      const response = (await API.graphql({
        query: getOrder,
        variables: {
          customerAccessToken: userAccessToken,
          id: `gid://shopify/Order/${orderId}`,
        },
      })) as GraphQLResult<{
        order: Order;
      }>;

      set(currentOrderAtom, response.data?.order);
    } catch (error) {
      console.error(error);
    }

    set(appLoadingAtom, false);
  },
);

export const selectedOrderIdAtom = atom<string | undefined>(undefined);
